"use strict";

export default function FlowModal() {

    // Backdrop
    var backdropElement = document.createElement("div");
    backdropElement.className = "fm-scrim hidden";

    // Modal Container
    var modalElement = backdropElement.appendChild(document.createElement("div"));
    modalElement.className = "fm-modal";

    // Header
    var headerContainer = modalElement.appendChild(document.createElement("div"));
    headerContainer.className = "fm-modal-head";
    headerContainer.style.cssText = "display:flex;align-items:flex-start;justify-content:space-between;gap:12px;";
    var headerText = headerContainer.appendChild(document.createElement("div"));
    headerText.style.flex = "1";
    this.headerElement = headerText.appendChild(document.createElement("h3"));
    this.headerElement.className = "m-0";
    var closeBtn = headerContainer.appendChild(document.createElement("button"));
    closeBtn.className = "modal-close-icon";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.innerHTML = '<i class="iconoir-xmark"></i>';
    closeBtn.addEventListener('click', this.close);

    // Content
    var bodyContainer = modalElement.appendChild(document.createElement("div"));
    bodyContainer.className = "fm-modal-body";
    this.messageElement = bodyContainer;

    // Footer
    this.footerElement = modalElement.appendChild(document.createElement("div"));
    this.footerElement.className = "fm-modal-foot";

    document.body.appendChild(backdropElement);

    this.clickedBtnId = null;
    this.backdropElement = backdropElement;
    this.modalElement = modalElement;

    // ── Turbo Drive awareness (F-15 follow-up) ───────────────────────────
    // This is a module singleton: the ES module (and this instance) survive
    // Turbo's <body> swaps, but the backdrop we appended to the *old* body does
    // not. After a soft navigation our backdrop — and any mounted-element state —
    // reference a detached, dead document. Re-attach lazily before use.
    this._reconnectIfDetached = function() {
        if (this.backdropElement.isConnected) return;

        // Any mount state points at the old (dead) body; discard it rather than
        // restoring into detached DOM. Our own header/body/footer (saved in
        // _savedChildren while an element is mounted) are still valid — put them
        // back so open()/mount() find the expected structure.
        if (this._savedChildren) {
            this.modalElement.innerHTML = '';
            this._savedChildren.forEach(function(c) { this.modalElement.appendChild(c); }.bind(this));
            this._savedChildren = null;
        }
        this._mountedEl = null;
        this._mountedParent = null;
        this._mountedNextSibling = null;
        this._mountOptions = null;

        this.backdropElement.classList.add('hidden');
        document.body.appendChild(this.backdropElement);
    }.bind(this);

    // Before Turbo caches the outgoing page, restore any mounted element to its
    // real slot so the snapshot isn't polluted with an open scrim / teleported
    // modal (which would restore as a dead ghost on the back button).
    document.addEventListener('turbo:before-cache', function() {
        if (this._mountedEl) this.unmount();
        // Pull the backdrop out of the cached snapshot entirely so restore visits
        // (back button) don't deposit an inert scrim; _reconnectIfDetached
        // re-appends a fresh one on the next open()/mount().
        this.backdropElement.remove();
    }.bind(this));

    // Mounted-element state for form modal consolidation
    this._mountedEl = null;
    this._mountedParent = null;
    this._mountedNextSibling = null;
    this._mountOptions = null;
    this._savedChildren = null;

    // Click scrim to cancel
    backdropElement.addEventListener('click', function(e) {
        if (e.target === backdropElement) this.close();
    }.bind(this));

    this._handleClose = function() {
        var cancelHook = this.options?.onCancelHook;
        var clickedBtnId = this.clickedBtnId;
        var btnHook = this.options?.onButtonHook;

        // Clean up before calling hooks
        delete this.options;
        this.clickedBtnId = null;
        this.footerElement.innerHTML = "";

        if (clickedBtnId !== null && typeof(btnHook) === 'function')
            btnHook(clickedBtnId);
        else if (typeof(cancelHook) === 'function')
            cancelHook();
    }.bind(this);

    this.close = function() {
        if (this._mountedEl) {
            this.unmount();
            return;
        }
        this.backdropElement.classList.add('hidden');
        this._handleClose();
    }.bind(this);

    // ── Mount/unmount for form modal consolidation ────────────────────────

    this.mount = function(el, options = {}) {
        this._reconnectIfDetached();
        if (this._mountedEl) {
            console.log('[FlowModal] auto-unmount before mount');
            this.unmount();
        }

        this._mountedEl = el;
        this._mountedParent = el.parentNode;
        this._mountedNextSibling = el.nextSibling;
        this._mountOptions = options;

        // Replace modal content with mounted element — avoids "modal inside modal"
        this._savedChildren = Array.from(this.modalElement.children);
        this.modalElement.innerHTML = '';
        this.modalElement.appendChild(el);

        // Wire .modal-close children to unmount
        var self = this;
        el.querySelectorAll('.modal-close').forEach(function(btn) {
            btn.addEventListener('click', function(e) {
                e.preventDefault();
                self.unmount();
            });
        });

        // Override Materialize .modal { display:none, position:fixed, etc. }
        el.style.display = 'block';
        el.style.position = 'relative';
        el.style.left = 'auto';
        el.style.right = 'auto';
        el.style.width = 'auto';
        el.style.maxHeight = 'none';
        el.style.margin = '0';
        el.style.overflowY = 'visible';

        // Fire onSetup once per element lifetime
        if (typeof options.onSetup === 'function' && !el._fmSetupDone) {
            options.onSetup();
            el._fmSetupDone = true;
        }

        this.backdropElement.classList.remove('hidden');

        console.log('[FlowModal] mounted', el.id || el.tagName);
    }.bind(this);

    this.unmount = function() {
        var el = this._mountedEl;
        if (!el) return;

        var options = this._mountOptions;

        // Restore element to original DOM position
        if (this._mountedParent) {
            if (this._mountedNextSibling && this._mountedNextSibling.parentNode === this._mountedParent) {
                this._mountedParent.insertBefore(el, this._mountedNextSibling);
            } else {
                this._mountedParent.appendChild(el);
            }
        }

        this._mountedEl = null;
        this._mountedParent = null;
        this._mountedNextSibling = null;
        this._mountOptions = null;

        // Restore FlowModal internal structure for open() usage
        if (this._savedChildren) {
            this.modalElement.innerHTML = '';
            this._savedChildren.forEach(function(c) { this.modalElement.appendChild(c); }.bind(this));
            this._savedChildren = null;
        }

        this.backdropElement.classList.add('hidden');

        // Reset Materialize overrides
        el.style.display = '';
        el.style.position = '';
        el.style.left = '';
        el.style.right = '';
        el.style.width = '';
        el.style.maxHeight = '';
        el.style.margin = '';
        el.style.overflowY = '';

        if (typeof options?.onClose === 'function') {
            options.onClose();
        }

        console.log('[FlowModal] unmounted', el.id || el.tagName);
    }.bind(this);

    // ── Confirmation-dialog open ─────────────────────────────────────────

    this.open = function(header, content, options = null) {
        this._reconnectIfDetached();
        if (this._mountedEl) {
            console.log('[FlowModal] auto-unmount before open');
            this.unmount();
        }

        this.headerElement.innerText = header || '';
        this.messageElement.innerHTML = typeof content === 'string' ? content : '';
        // Clear footer up front: normally _handleClose() does this on close, but a
        // dialog cached mid-nav (back button) restores with its buttons intact, so
        // the next open() would stack duplicates on top.
        this.footerElement.innerHTML = '';

        this.options = Object.assign({}, FlowModal.default_options, options);

        for (var b of this.options.buttons) {
            var bElem = this.footerElement.appendChild(document.createElement("button"));
            if (b.primary || b.id === true) {
                bElem.className = "bg-neo-accent text-neo-text-on-accent font-bold px-[22px] h-12 border-neo border-neo-border rounded-neo shadow-neo hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-neo-sm transition-all flex items-center gap-2";
            } else if (b.danger) {
                bElem.className = "bg-neo-danger text-white font-bold px-[22px] h-12 border-neo border-neo-danger rounded-neo shadow-neo-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all flex items-center gap-2";
            } else {
                bElem.className = "bg-white text-neo-text font-bold px-[22px] h-12 border-neo border-neo-border rounded-neo shadow-neo-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all flex items-center gap-2";
            }

            bElem.innerText = b.text;
            bElem.addEventListener('click', function(bid) {
                this.clickedBtnId = bid;
                this.close();
            }.bind(this, b.id));
        }

        this.backdropElement.classList.remove('hidden');
    };
};

Object.defineProperty(FlowModal, 'default_options', {
    get() {
        return {
            buttons: [
                { id: true, text: TR("btn.Ok") }
            ]
        };
    },
    configurable: true,
    enumerable: true
});

FlowModal.Instance = null;
FlowModal.getInstance = function() {
    if (!FlowModal.Instance)
        FlowModal.Instance = new FlowModal();

    return FlowModal.Instance;
};